import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Icon, Input } from "@/ds";
import { anonymousKeyFor, executeAction, rejectAction } from "@/lib/dany";

/**
 * Handing a case to a person, as a card in the conversation (DY2.4).
 *
 * DANY.md §2.7 says Dany "detects the case, summarises the problem, confirms
 * with the user, and creates the ticket". This component is the third of those
 * four verbs, and it is the one that makes the fourth safe: the `action_id` is
 * the whole authorization and Send is the commit point, so nobody gets a ticket
 * opened in their name because a model read them as upset.
 *
 * **The summary is editable, and that is the point of showing it.** Dany wrote
 * it from the conversation; the person whose problem it is gets to correct it
 * before it reaches the queue. What they cannot edit — because it never reaches
 * the browser as an editable field — is the category, the trigger and the reply
 * address: `execute_action` reads those three back off the proposal row and
 * drops whatever the widget sends for them. The email is *shown*, because a
 * promise about where the answer lands is worth reading before you accept it.
 *
 * **Two verbs, one card.** With an open case in the same category already in
 * the queue, the backend proposes `mode: "append"` instead, and the card says
 * so: adding to the case they already have, rather than opening a second one
 * nobody would connect to the first.
 *
 * **The honest expectation is in the copy, not in the tone.** "The reply comes
 * by email and it isn't instant" sits under the button, where someone deciding
 * whether to press it will read it — not in the confirmation afterwards, where
 * it would be an excuse rather than an expectation.
 */
export default function DanyTicketWidget({ data, threadId, onCommitted }) {
  const { t } = useTranslation();
  const [subject, setSubject] = React.useState(data?.subject || "");
  const [summary, setSummary] = React.useState(data?.summary || "");
  const [busy, setBusy] = React.useState(false);
  const [phase, setPhase] = React.useState(data?.sent ? "sent" : "draft");
  const [error, setError] = React.useState(null);
  const [ticketId, setTicketId] = React.useState(data?.ticket_id || null);

  const actionId = data?.action_id;
  const appending = data?.mode === "append";
  const email = data?.contact_email;

  const send = async () => {
    if (!actionId) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await executeAction(actionId, { subject, summary }, {
        anonymousKey: anonymousKeyFor(threadId),
      });
      setTicketId(answer?.ticket_id || null);
      setPhase("sent");
      // The thread re-reads itself: this card is not the only thing on
      // screen that this press changed (T11 N38).
      onCommitted?.();
    } catch (err) {
      setError(
        t([`dany.ticket.${err.code}`, `dany.errors.${err.code}`,
           "dany.ticket.failed"], {
          defaultValue: t("dany.ticket.failed"),
        })
      );
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await rejectAction(actionId, { anonymousKey: anonymousKeyFor(threadId) });
    } catch { /* the card goes away either way */ }
    setPhase("dismissed");
    // The thread re-reads itself: this card is not the only thing on
    // screen that this press changed (T11 N38).
    onCommitted?.();
    setBusy(false);
  };

  if (phase === "dismissed") return null;

  if (phase === "sent") {
    return (
      <Card variant="raised" padding="md" data-testid="dany-ticket-sent">
        <p style={heading}>
          {appending ? t("dany.ticket.addedTitle") : t("dany.ticket.sentTitle")}
        </p>
        <p style={body}>{t("dany.ticket.sentBody", { email })}</p>
        {ticketId && (
          <p style={{ ...meta, marginTop: "var(--space-3)" }}>
            {t("dany.ticket.reference", { id: ticketId })}
          </p>
        )}
      </Card>
    );
  }

  return (
    <Card variant="raised" padding="md" data-testid="dany-ticket">
      <span style={eyebrow}>
        <Icon name="user" size={12} style={{ color: "var(--hive-ink)" }} />
        {appending ? t("dany.ticket.headerAppend") : t("dany.ticket.header")}
      </span>
      <p style={body}>
        {appending ? t("dany.ticket.bodyAppend") : t("dany.ticket.body")}
      </p>

      {/* Only on a new case. An append inherits the subject of the ticket it
          joins — letting it be retitled here would rename a case the team is
          already working from. */}
      {!appending && (
        <label style={{ display: "block", marginTop: "var(--space-3)" }}>
          <span style={fieldLabel}>{t("dany.ticket.fieldSubject")}</span>
          <Input
            value={subject} maxLength={120}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="dany-ticket-subject"
          />
        </label>
      )}

      <label style={{ display: "block", marginTop: "var(--space-3)" }}>
        <span style={fieldLabel}>{t("dany.ticket.fieldSummary")}</span>
        <textarea
          className="input-base"
          rows={5}
          value={summary}
          maxLength={4000}
          onChange={(e) => setSummary(e.target.value)}
          data-testid="dany-ticket-summary"
          style={{ width: "100%", resize: "vertical" }}
        />
      </label>

      {email && (
        <p style={{ ...meta, marginTop: "var(--space-3) " }}
           data-testid="dany-ticket-email">
          {t("dany.ticket.replyTo", { email })}
        </p>
      )}

      {error && (
        <p style={{ ...body, color: "var(--danger)" }}
           data-testid="dany-ticket-error">{error}</p>
      )}

      <div style={{
        marginTop: "var(--space-4)", display: "flex", gap: "var(--space-2)",
        flexWrap: "wrap",
      }}>
        <Button variant="primary" size="sm" icon="check"
                disabled={busy || !actionId || !summary.trim()} onClick={send}
                data-testid="dany-ticket-send">
          {appending ? t("dany.ticket.add") : t("dany.ticket.send")}
        </Button>
        <Button variant="ghost" size="sm" icon="x" disabled={busy}
                onClick={dismiss} data-testid="dany-ticket-skip">
          {t("dany.ticket.skip")}
        </Button>
      </div>
      <p style={{ ...meta, marginTop: "var(--space-3)" }}>
        {t("dany.ticket.expectation")}
      </p>
    </Card>
  );
}

const fieldLabel = {
  display: "block", marginBottom: 4, fontFamily: "var(--font-display)",
  fontSize: "var(--text-xs)", textTransform: "uppercase",
  letterSpacing: "var(--tracking-widest)", color: "var(--dany-meta)",
};

const eyebrow = {
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-display)", fontSize: "var(--text-xs)",
  textTransform: "uppercase", letterSpacing: "var(--tracking-widest)",
  color: "var(--dany-meta)",
};

const heading = {
  fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
  fontSize: "var(--text-lg)", color: "var(--text-body)",
};

const body = {
  marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
  color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
  maxWidth: "var(--measure-body)",
};

const meta = {
  fontSize: "var(--text-xs)", color: "var(--dany-meta)",
  lineHeight: "var(--leading-relaxed)",
};
